import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import httpClient from "../helpers/httpClient";

const initialState = {
  products: {
    data: [],
    loading: false,
    error: null,
  },
  selectedProduct: {
    data: {},
    loading: false,
  },
  topSellingProducts: {
    data: [],
    loading: false,
  },
};

export const getProducts = createAsyncThunk(
  "products/getProducts",
  async () => {
    try {
      const response = await httpClient.get("/web/products");
      return response.data.res;
    } catch (error) {
      const customError = {
        name: "Custom axios error",
        message: error.response.data.message,
        data: error.response.data,
      };
      throw customError;
    }
  }
);

export const addProduct = createAsyncThunk(
  "products/addProduct",
  async (product) => {
    try {
      const response = await httpClient.post("/web/products", product);
      return response.data.res;
    } catch (error) {
      const customError = {
        name: "Custom axios error",
        message: error.response.data.message,
        data: error.response.data,
      };
      throw customError;
    }
  }
);

export const getProductById = createAsyncThunk(
  "products/getProductById",
  async (id) => {
    try {
      const response = await httpClient.get(`/web/products/${id}`);
      return response.data.res;
    } catch (error) {
      const customError = {
        name: "Custom axios error",
        message: error.response.data.message,
        data: error.response.data,
      };
      throw customError;
    }
  }
);

export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async (product) => {
    try {
      const response = await httpClient.put(
        `/web/products/${product._id}`,
        product.data
      );
      return response.data.res;
    } catch (error) {
      const customError = {
        name: "Custom axios error",
        message: error.response.data.message,
        data: error.response.data,
      };
      throw customError;
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (id) => {
    try {
      await httpClient.delete(`/web/products/${id}`);
      return id;
    } catch (error) {
      const customError = {
        name: "Custom axios error",
        message: error.response.data.message,
        data: error.response.data,
      };
      throw customError;
    }
  }
);

export const getTopSellingProducts = createAsyncThunk(
  "products/getTopSellingProducts",
  async (topX) => {
    try {
      const response = await httpClient.get(
        "/web/products-stats/top-selling?topX=" + topX
      );
      return response.data.res;
    } catch (error) {
      const customError = {
        name: "Custom axios error",
        message: error.response.data.message,
        data: error.response.data,
      };
      throw customError;
    }
  }
);

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    resetSelectedProduct: (state) => {
      state.selectedProduct = {
        data: {},
        loading: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProducts.pending, (state) => {
        state.products.loading = true;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.products.loading = false;
        state.products.data = action.payload;
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.products.loading = false;
        state.products.error = action.error;
      })
      .addCase(addProduct.pending, (state) => {
        state.products.loading = true;
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.products.loading = false;
        state.products.data.push(action.payload);
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.products.loading = false;
        state.products.error = action.error;
      })
      .addCase(getProductById.pending, (state) => {
        state.selectedProduct.loading = true;
      })
      .addCase(getProductById.fulfilled, (state, action) => {
        state.selectedProduct.loading = false;
        state.selectedProduct.data = action.payload;
      })
      .addCase(getProductById.rejected, (state, action) => {
        state.selectedProduct.loading = false;
        state.selectedProduct.error = action.error;
      })
      .addCase(updateProduct.pending, (state) => {
        state.products.loading = true;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.products.loading = false;
        state.products.data = state.products.data.map((product) =>
          product._id === action.payload._id ? action.payload : product
        );
      })
      .addCase(updateProduct.rejected, (state) => {
        state.products.loading = false;
      })
      .addCase(deleteProduct.pending, (state) => {
        state.products.loading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products.loading = false;
        state.products.data = state.products.data.filter(
          (product) => product._id !== action.payload
        );
      })
      .addCase(deleteProduct.rejected, (state) => {
        state.products.loading = false;
      })
      .addCase(getTopSellingProducts.pending, (state) => {
        state.topSellingProducts.loading = true;
      })
      .addCase(getTopSellingProducts.fulfilled, (state, action) => {
        state.topSellingProducts.loading = false;
        state.topSellingProducts.data = action.payload;
      })
      .addCase(getTopSellingProducts.rejected, (state) => {
        state.topSellingProducts.loading = false;
      });
  },
});

export const { resetSelectedProduct } = productSlice.actions;

export default productSlice.reducer;
